import { Plus, Lock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { StopEditor } from "@/components/admin/StopEditor";

export const EMPTY_STOP = (type) => ({ type, company: "", address: "", date: "", time: "", reference: "", contact: "", notes: "" });

export const EMPTY_ORDER = {
  reference: "", partner: "", source: "", stops: [EMPTY_STOP("loading"), EMPTY_STOP("unloading")],
  cargo: "", weight: "", loading_meters: "", pallets: "", vehicle: "", driver_notes: "",
  price: null, currency: "EUR", driver_id: null, pdf_file_id: null,
};

const Field = ({ label, children, className = "" }) => (
  <label className={`block space-y-1 ${className}`}>
    <span className="text-xs text-slate-400">{label}</span>
    {children}
  </label>
);

export function OrderForm({ value, onChange, drivers }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  const setStops = (stops) => set("stops", stops);
  const move = (i, d) => {
    const s = [...value.stops];
    [s[i], s[i + d]] = [s[i + d], s[i]];
    setStops(s);
  };
  const text = (k, label, cls) => (
    <Field label={label} className={cls}>
      <Input value={value[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`order-${k}-input`} />
    </Field>
  );

  return (
    <div className="space-y-6" data-testid="order-form">
      <section className="panel space-y-4">
        <div className="eyebrow">Șofer</div>
        <select value={value.driver_id || ""} onChange={(e) => set("driver_id", e.target.value || null)} data-testid="order-driver-select"
          className="h-11 w-full rounded-md border border-white/10 bg-[#0A0E17] px-3 text-base">
          <option value="">— Alege șoferul —</option>
          {drivers.filter((d) => d.active || d.id === value.driver_id).map((d) => (
            <option key={d.id} value={d.id}>{d.name}{d.truck ? ` · ${d.truck}` : ""} ({d.language.toUpperCase()})</option>
          ))}
        </select>
      </section>

      <section className="panel space-y-4">
        <div className="eyebrow">Comandă</div>
        <div className="grid gap-3 sm:grid-cols-3">
          {text("reference", "Referință comandă")}
          {text("partner", "Partener / client (doar birou)")}
          {text("source", "Sursă (Timocom, Trans.eu...)")}
        </div>
      </section>

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="eyebrow">Opriri ({value.stops.length})</div>
          <div className="flex gap-2">
            {["loading", "unloading"].map((t) => (
              <button key={t} type="button" onClick={() => setStops([...value.stops, EMPTY_STOP(t)])} data-testid={`add-stop-${t}`}
                className="inline-flex items-center gap-1 rounded-full border border-white/10 px-3 py-1.5 text-xs transition-colors hover:bg-white/5">
                <Plus className="h-3.5 w-3.5" /> {t === "loading" ? "Încărcare" : "Descărcare"}
              </button>
            ))}
          </div>
        </div>
        {value.stops.map((s, i) => (
          <StopEditor key={i} stop={s} index={i} total={value.stops.length}
            onChange={(ns) => setStops(value.stops.map((x, j) => (j === i ? ns : x)))}
            onMove={(d) => move(i, d)} onRemove={() => setStops(value.stops.filter((_, j) => j !== i))} />
        ))}
      </section>

      <section className="panel space-y-4">
        <div className="eyebrow">Marfă și vehicul</div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {text("cargo", "Marfă", "lg:col-span-2")}
          {text("weight", "Greutate")}
          {text("loading_meters", "Metri liniari")}
          {text("pallets", "Paleți")}
          {text("vehicle", "Cerințe vehicul", "lg:col-span-3")}
        </div>
        <Field label="Mesaj pentru șofer">
          <Textarea rows={3} value={value.driver_notes || ""} onChange={(e) => set("driver_notes", e.target.value)} data-testid="order-driver_notes-input" />
        </Field>
      </section>

      <section className="rounded-xl border border-amber-500/30 bg-amber-500/[0.05] p-5" data-testid="price-section">
        <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-amber-400">
          <Lock className="h-3.5 w-3.5" /> Doar birou · șoferul nu vede prețul
        </div>
        <div className="mt-3 grid max-w-sm grid-cols-3 gap-3">
          <Field label="Preț" className="col-span-2">
            <Input type="number" step="0.01" value={value.price ?? ""} onChange={(e) => set("price", e.target.value === "" ? null : Number(e.target.value))} data-testid="order-price-input" />
          </Field>
          {text("currency", "Monedă")}
        </div>
      </section>
    </div>
  );
}
